// Recrutement v2 — briques visuelles : jauge par dimension avec seuil,
// verdict de barre, radar 4 axes (candidat vs barre), notation 1–5 par
// dimension avec preuve écrite, portes de l'offre. Styles dans
// styles/recrutement-v2.css (mêmes tokens que l'île .recapp).
import {
  REC_DIMS, recNeedsEvidence,
  type RecBar, type RecDim, type RecDimScore, type RecGridCrit, type RecSettings,
} from '@pointage/shared';

export const fmt1 = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? '—' : (Math.round(v * 10) / 10).toFixed(1).replace('.', ',');

export function V2Ic({ k, s = 18, w = 1.8 }: { k: string; s?: number; w?: number }) {
  const P: Record<string, React.ReactNode> = {
    TEST: <><path d="M9 3.5h6" /><path d="M10 3.5v5.2L4.8 17.6A2 2 0 006.5 20.5h11a2 2 0 001.7-2.9L14 8.7V3.5" /><path d="M7.5 14.5h9" /></>,
    VERIF: <><path d="M12 3.5l7 2.5v5.5c0 4.4-3 7.7-7 9-4-1.3-7-4.6-7-9V6z" /><path d="M9 11.5l2 2 4-4.5" /></>,
    check: <path d="M5 12.5l4.5 4.5L19 7" />,
    lock: <><rect x="5" y="10.5" width="14" height="10" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 018 0v3" /></>,
    unlock: <><rect x="5" y="10.5" width="14" height="10" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 017.6-1.7" /></>,
    target: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.8" /><circle cx="12" cy="12" r="1.3" /></>,
    up: <path d="M12 19V5M6 11l6-6 6 6" />,
    down: <path d="M12 5v14M6 13l6 6 6-6" />,
    clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
    doc: <><path d="M6.5 3.5h7l4 4v13h-11z" /><path d="M13.5 3.5v4h4" /></>,
    inbox: <><path d="M4 13l2.5-7h11L20 13v5.5H4z" /><path d="M4 13h4.5a3.5 3.5 0 007 0H20" /></>,
    star: <path d="M12 4l2.4 5 5.4.6-4 3.7 1.1 5.4L12 16l-4.9 2.7 1.1-5.4-4-3.7 5.4-.6z" />,
    team: <><circle cx="9" cy="8.5" r="3" /><circle cx="16.5" cy="9.5" r="2.4" /><path d="M3.5 19a5.5 5.5 0 0111 0" /><path d="M14.5 14.5a4.5 4.5 0 016 4.5" /></>,
    bell: <><path d="M6 16.5V11a6 6 0 0112 0v5.5l1.5 2h-15z" /><path d="M10 20.5a2 2 0 004 0" /></>,
    gavel: <><path d="M14.5 4.5l5 5" /><path d="M11.5 7.5l5 5" /><path d="M13 6l-4.5 4.5M17.5 10.5L13 15" /><path d="M10.5 12.5L4 19" /><path d="M3.5 20.5h8" /></>,
    flag: <><path d="M5.5 21V4" /><path d="M5.5 4.5h12.5l-2.6 4 2.6 4H5.5" /></>,
  };
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {P[k] ?? null}
    </svg>
  );
}

const thrOf = (dim: RecDim, st: Pick<RecSettings, 'barPerf' | 'barMin'>) => (dim === 'PERFORMANCE' ? st.barPerf : st.barMin);

export function DimMeter({ s, st, sm }: { s: RecDimScore; st: RecSettings; sm?: boolean }) {
  return (
    <div className={`rv2-meter${sm ? '' : ' withthr'}`}>
      {REC_DIMS.map((d, i) => {
        const v = s.dims[d.id];
        const t = thrOf(d.id, st);
        return (
          <div key={d.id} className={`rv2-mr${sm ? ' sm' : ''} ${v == null ? 'empty' : v < t ? 'ko' : 'ok'}`} style={{ '--dc': d.c } as React.CSSProperties} title={`${d.lbl} — seuil ${fmt1(t)}`}>
            <span className="l"><i></i>{sm ? d.short : d.lbl}</span>
            <span className="tr">
              <span className="f" style={{ width: `${((v ?? 0) / 5) * 100}%`, animationDelay: `${i * 70}ms` }}></span>
              <span className="t" data-v={fmt1(t)} style={{ left: `${(t / 5) * 100}%` }}></span>
            </span>
            <span className="v">{fmt1(v)}</span>
          </div>
        );
      })}
    </div>
  );
}

export const barWhy = (bar: RecBar | null, st: RecSettings) =>
  !bar || bar.state === 'pend' ? 'Toutes les notes du jury sont nécessaires.'
    : bar.state === 'ko' ? bar.fails.join(' · ')
      : `Global ≥ ${fmt1(st.barOverall)} · Performance ≥ ${fmt1(st.barPerf)} · aucune dimension < ${fmt1(st.barMin)}`;

export function BarPill({ bar, overridden, compact, st }: { bar: RecBar | null; overridden?: boolean; compact?: boolean; st?: RecSettings }) {
  const state = bar?.state ?? 'pend';
  const cls = overridden && state === 'ko' ? 'ov' : state;
  const lbl = state === 'ok' ? 'Au-dessus de la barre' : state === 'ko' ? (overridden ? 'Sous la barre · dérogation' : 'Sous la barre') : 'Barre en attente';
  return (
    <span className={`rv2-bar ${cls}`} title={st ? barWhy(bar, st) : undefined}>
      <V2Ic k={state === 'ok' ? 'up' : state === 'ko' ? 'down' : 'target'} s={12} w={2.6} />
      {compact ? (state === 'ok' ? 'Barre ✓' : state === 'ko' ? 'Sous la barre' : 'Barre ?') : lbl}
    </span>
  );
}

export function BarCard({ s, bar, st, title, overridden }: { s: RecDimScore; bar: RecBar | null; st: RecSettings; title: string; overridden?: string | null }) {
  const state = bar?.state ?? 'pend';
  const c = state === 'ok' ? 'var(--green)' : state === 'ko' ? 'var(--bad)' : 'var(--ink3)';
  return (
    <div className={`rv2-barcard ${state}`} style={{ '--bc': c } as React.CSSProperties}>
      <div className="hd">
        <div><div className="k">{title}</div><div className="num">{fmt1(s.overall)}<i>/5</i></div></div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <BarPill bar={bar} overridden={!!overridden} />
          <div className="why">{overridden ? `Dérogation BOSS — « ${overridden} »` : barWhy(bar, st)}</div>
        </div>
      </div>
      <DimMeter s={s} st={st} />
    </div>
  );
}

export function Radar({ s, st, color, size = 200 }: { s: RecDimScore; st: RecSettings; color: string; size?: number }) {
  const R = size / 2 - 26;
  const cx = size / 2, cy = size / 2;
  const ang = (i: number) => -Math.PI / 2 + (i * Math.PI) / 2;
  const pt = (i: number, v: number) => [cx + Math.cos(ang(i)) * R * (v / 5), cy + Math.sin(ang(i)) * R * (v / 5)] as const;
  const poly = (vals: number[]) => vals.map((v, i) => pt(i, v).join(',')).join(' ');
  const vals = REC_DIMS.map((d) => s.dims[d.id] ?? 0);
  const thr = REC_DIMS.map((d) => thrOf(d.id, st));
  return (
    <svg className="rv2-radar" width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ '--rc': color } as React.CSSProperties} role="img" aria-label="Profil par dimension comparé à la barre">
      {[1, 2, 3, 4, 5].map((l) => <polygon key={l} className="grid" points={poly([l, l, l, l])} opacity={l === 5 ? 1 : 0.6} />)}
      {REC_DIMS.map((_, i) => { const [x, y] = pt(i, 5); return <line key={i} className="ax" x1={cx} y1={cy} x2={x} y2={y} />; })}
      <polygon className="barpoly" points={poly(thr)} />
      <polygon className="cand" points={poly(vals)} />
      {REC_DIMS.map((d, i) => { const [x, y] = pt(i, vals[i]!); return <circle key={d.id} className={`pt${vals[i]! < thr[i]! ? ' ko' : ''}`} cx={x} cy={y} r={3.6} stroke={color} />; })}
      {REC_DIMS.map((d, i) => {
        const [x, y] = pt(i, 6.1);
        const anchor = i === 1 ? 'start' : i === 3 ? 'end' : 'middle';
        const dx = i === 1 ? -14 : i === 3 ? 14 : 0;
        const dy = i === 0 ? -2 : i === 2 ? 9 : 3;
        return (
          <g key={d.id}>
            <text x={x + dx} y={y + dy} textAnchor={anchor} fill={d.c}>{d.short}</text>
            <text className="v" x={x + dx} y={y + dy + 11} textAnchor={anchor}>{fmt1(s.dims[d.id])}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Notation 1–5 groupée par dimension, preuve écrite exigée pour 1, 2, 5. */
export function GradeGroups({ grid, scores, evidence, onScore, onEv, readOnly, refScores, showEvidence = true }: {
  grid: RecGridCrit[];
  scores: Record<string, number>;
  evidence?: Record<string, string>;
  onScore?: (id: string, v: number) => void;
  onEv?: (id: string, v: string) => void;
  readOnly?: boolean;
  refScores?: Record<string, number>;
  showEvidence?: boolean;
}) {
  const ev = evidence ?? {};
  const noDim = grid.filter((g) => !g.dim);
  const groups = [...REC_DIMS.map((d) => ({ d, rows: grid.filter((g) => g.dim === d.id) })), ...(noDim.length ? [{ d: { id: 'X', lbl: 'Sans dimension', short: '?', c: '#74747e', hint: 'à classer au catalogue' }, rows: noDim }] : [])];
  return (
    <div>
      {groups.map(({ d, rows }, gi) => {
        if (!rows.length) return null;
        const scored = rows.filter((g) => scores[g.id] != null);
        const co = rows.reduce((a, g) => a + g.co, 0);
        const avg = scored.length ? scored.reduce((a, g) => a + scores[g.id]! * g.co, 0) / scored.reduce((a, g) => a + g.co, 0) : null;
        return (
          <div key={d.id} className="rv2-dgroup" style={{ '--dc': d.c, animationDelay: `${gi * 70}ms` } as React.CSSProperties}>
            <div className="rv2-dgh">
              <span className="ic">{d.lbl[0]}</span>
              <div><b>{d.lbl}</b> <span>· {d.hint} · coef {co}</span></div>
              <span className="v">{fmt1(avg)}</span>
            </div>
            {rows.map((g) => {
              const v = scores[g.id];
              const need = recNeedsEvidence(v);
              const txt = ev[g.id] ?? '';
              return (
                <div key={g.id} className="rv2-crit">
                  <div className="r1">
                    <span className="lb">{g.lbl}<span className="cf">×{g.co}</span></span>
                    <span className="rv2-pips">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" disabled={readOnly}
                          className={`rv2-pip${v === n ? ` on${n <= 2 ? ' lo' : ''}` : ''}${refScores && Math.round(refScores[g.id] ?? 0) === n ? ' ref' : ''}`}
                          onClick={() => onScore?.(g.id, n)} aria-label={`${g.lbl} : ${n} sur 5`}>{n}</button>
                      ))}
                    </span>
                  </div>
                  {showEvidence && !readOnly && v != null && (
                    <div className={`rv2-ev${need ? (txt.trim() ? ' ok' : '') : ' opt'}`}>
                      <div className="lab">{need ? (txt.trim() ? '✓ preuve écrite' : `Preuve obligatoire pour un ${v}`) : 'Preuve (facultative pour 3 et 4)'}</div>
                      <textarea rows={2} value={txt} placeholder={need ? 'Un fait observé, une phrase, un exemple vérifiable…' : 'Ce qui justifie la note (facultatif)'} onChange={(e) => onEv?.(g.id, e.target.value)}></textarea>
                    </div>
                  )}
                  {showEvidence && readOnly && txt && <div className="rv2-ev-read">{txt}</div>}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export interface Gate { k: string; lbl: string; ok: boolean; ov?: boolean; tx: string; rule: string; hard?: boolean; action?: React.ReactNode }
export function Gates({ gates }: { gates: Gate[] }) {
  const n = gates.filter((g) => g.ok || g.ov).length;
  const allOk = n === gates.length;
  return (
    <div>
      <div className="rv2-gatehead" style={{ '--gc': allOk ? 'var(--green)' : 'var(--bad)' } as React.CSSProperties}>
        <span className="n">{n}<i>/{gates.length}</i></span>
        <div>
          <b>{allOk ? 'Toutes les portes sont ouvertes' : `${gates.length - n} porte${gates.length - n > 1 ? 's' : ''} fermée${gates.length - n > 1 ? 's' : ''}`}</b>
          <span>{allOk ? 'L’offre peut être construite et soumise à validation.' : '« Construire l’offre » reste verrouillé tant qu’une porte est fermée.'}</span>
        </div>
        <span className="rv2-gatebar">{gates.map((g) => <i key={g.k} className={g.ok ? 'on' : g.ov ? 'ov' : ''}></i>)}</span>
      </div>
      <div className="rv2-gates">
        {gates.map((g, i) => (
          <div key={g.k} className={`rv2-gate ${g.ok ? 'ok' : g.ov ? 'ov' : 'ko'}`} style={{ animationDelay: `${i * 60}ms` }}>
            <span className="lk"><V2Ic k={g.ok || g.ov ? 'unlock' : 'lock'} s={17} w={2} /></span>
            <div className="tx"><b>{g.lbl}</b><span>{g.tx}</span></div>
            <span className={`rule${g.hard ? ' hard' : ''}`}>{g.rule}</span>
            {g.action}
          </div>
        ))}
      </div>
    </div>
  );
}

export function YesNo({ v, onSet, disabled }: { v: boolean | null | undefined; onSet: (v: boolean) => void; disabled?: boolean }) {
  return (
    <span className="rv2-yn">
      <button type="button" disabled={disabled} className={`y${v === true ? ' on' : ''}`} onClick={() => onSet(true)}>Oui</button>
      <button type="button" disabled={disabled} className={`n${v === false ? ' on' : ''}`} onClick={() => onSet(false)}>Non</button>
    </span>
  );
}
export function Seg<T extends string>({ v, opts, onSet, disabled }: { v: T | null | undefined; opts: Array<[T, string]>; onSet: (v: T) => void; disabled?: boolean }) {
  return (
    <span className="rv2-yn">
      {opts.map(([k, l]) => <button key={k} type="button" disabled={disabled} className={`m${v === k ? ' on' : ''}`} onClick={() => onSet(k)}>{l}</button>)}
    </span>
  );
}
